import { useState } from "react";
import { OpenNowBadge } from "./OpenNow";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PageHeader, Badge, Button } from "@/components/ui/PageHeader";
import { InputField, SelectField } from "@/components/ui/FormFields";
import {
  listProviders,
  getProviderCategories,
  getProviderAsks,
  type ProviderListItem,
  type ListProvidersParams,
} from "@/api/providers";

export default function ProvidersLab() {
  const [filters, setFilters] = useState<ListProvidersParams>({ limit: 24 });
  const [query, setQuery] = useState("");

  const { data: cats } = useQuery({
    queryKey: ["provider-categories"],
    queryFn: getProviderCategories,
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["providers", filters, query],
    queryFn: () => listProviders({ ...filters, q: query || undefined }),
  });

  const categoryOptions = [
    { value: "", label: "All categories" },
    ...(cats?.data ?? []).map((c) => ({ value: c.code, label: c.name })),
  ];

  // Subcategories of the selected category ("nanny" under childcare,
  // "pandit" under religious). Empty until a category is chosen.
  const selectedCat = (cats?.data ?? []).find((c) => c.code === filters.category);
  const subcategoryOptions = [
    { value: "", label: selectedCat ? "All types" : "Pick a category first" },
    ...(selectedCat?.subcategories ?? []).map((s) => ({ value: s.code, label: s.name })),
  ];

  // Skill chips for the chosen category. Selected skills live in the
  // `skills` param as a comma-joined string (matches ANY, server-side).
  const suggestedSkills = selectedCat?.suggestedSkills ?? [];
  const activeSkills = (filters.skills ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const toggleSkill = (skill: string) => {
    const next = activeSkills.includes(skill)
      ? activeSkills.filter((s) => s !== skill)
      : [...activeSkills, skill];
    setFilters({ ...filters, skills: next.length ? next.join(",") : undefined });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Providers Directory"
        subtitle="Trust-based service discovery — Pandits, nannies, CPAs, immigration lawyers, tiffin cooks, and more. Surfaced via vouches."
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Providers" }]}
        actions={
          <Link to="/lab/providers/new">
            <Button>+ List your service</Button>
          </Link>
        }
      />

      <AsksWaiting />

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <InputField
            label="Search"
            placeholder="Name or description"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <SelectField
            label="Category"
            value={filters.category ?? ""}
            onChange={(e) =>
              // Changing category clears a now-invalid subcategory.
              setFilters({
                ...filters,
                category: e.target.value || undefined,
                subcategory: undefined,
              })
            }
            options={categoryOptions}
          />
          <SelectField
            label="Type"
            value={filters.subcategory ?? ""}
            onChange={(e) =>
              setFilters({ ...filters, subcategory: e.target.value || undefined })
            }
            options={subcategoryOptions}
          />
          <InputField
            label="From (origin state)"
            placeholder="Gujarat, Tamil Nadu…"
            value={filters.originState ?? ""}
            onChange={(e) =>
              setFilters({ ...filters, originState: e.target.value || undefined })
            }
          />
          <InputField
            label="City"
            value={filters.city ?? ""}
            onChange={(e) =>
              setFilters({ ...filters, city: e.target.value || undefined })
            }
          />
          <InputField
            label="State (where they are)"
            value={filters.state ?? ""}
            onChange={(e) =>
              setFilters({ ...filters, state: e.target.value || undefined })
            }
          />
          <SelectField
            label="Verification"
            value={filters.verified ? "true" : ""}
            onChange={(e) =>
              setFilters({ ...filters, verified: e.target.value === "true" || undefined })
            }
            options={[
              { value: "", label: "Any" },
              { value: "true", label: "Verified only" },
            ]}
          />
        </div>

        {/* Skill chips — appear once a category with suggestions is chosen. */}
        {filters.category && suggestedSkills.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-2">
              Filter by skill
            </p>
            <div className="flex flex-wrap gap-2">
              {suggestedSkills.map((s) => {
                const on = activeSkills.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleSkill(s)}
                    className={
                      on
                        ? "px-3 py-1.5 rounded-full text-xs bg-primary-600 text-white"
                        : "px-3 py-1.5 rounded-full text-xs border border-gray-200 text-gray-700 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                    }
                  >
                    {on ? "✓ " : ""}
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
          Failed to load providers.{" "}
          <button onClick={() => refetch()} className="underline font-medium">
            Retry
          </button>
        </div>
      ) : !data || data.data.length === 0 ? (
        <p className="text-sm text-gray-500">No providers match these filters.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.data.map((p) => (
              <ProviderCard key={p.id} p={p} />
            ))}
          </div>
          {data.meta && (
            <p className="text-xs text-gray-400">
              Showing {data.data.length} of {data.meta.total}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function ProviderCard({ p }: { p: ProviderListItem }) {
  return (
    <Link
      to={`/lab/providers/${p.slug}`}
      className="block bg-white border border-gray-200 rounded-2xl p-4 hover:border-primary-300 hover:shadow-md transition-all"
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center text-xl shrink-0">
          {p.category.icon ? null : "🛠️"}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 truncate">{p.name}</h3>
            {p.isVerified && <Badge variant="green">verified</Badge>}
            {p.forYourNeed && <Badge variant="purple">for your need</Badge>}
            <OpenNowBadge openNow={p.openNow} />
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {p.category.name}
            {p.subcategory ? ` · ${p.subcategory.name}` : ""} · {p.city},{" "}
            {p.state}
          </p>
          <div className="flex gap-3 mt-2 text-xs text-gray-600">
            <span>{p.vouchCount} vouches</span>
            {p.avgRating > 0 && <span>★ {p.avgRating.toFixed(1)}</span>}
            {p.mentionCount > 0 && <span>{p.mentionCount} mentions</span>}
          </div>
          {p.skills.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {p.skills.slice(0, 4).map((s) => (
                <span
                  key={s}
                  className="px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-600"
                >
                  {s}
                </span>
              ))}
              {p.skills.length > 4 && (
                <span className="text-[11px] text-gray-400">
                  +{p.skills.length - 4}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}


/**
 * Questions waiting on a business, grouped by trade.
 *
 * `totalUnanswered` is the recruitment number, and it points the opposite way
 * to everything else on this page. The directory shows who IS here; this shows
 * where somebody asked and nobody could answer — which is a trade with nobody
 * in it, and the only signal that says which shop to go and find next.
 */
function AsksWaiting() {
  const { data, isLoading } = useQuery({
    queryKey: ["provider-asks"],
    queryFn: () => getProviderAsks({ limit: 20 }),
  });

  if (isLoading) return null;
  const d = data?.data;
  if (!d) return null;

  const cats = (d.categories ?? []).filter(
    (c) => (c.unansweredCount ?? 0) > 0 || (c.asks?.length ?? 0) > 0,
  );

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5">
      <div className="flex items-baseline gap-2 flex-wrap mb-1">
        <h2 className="font-semibold text-gray-900">People are asking</h2>
        <span className="text-sm text-gray-500">
          {d.meta.totalAsks} question{d.meta.totalAsks === 1 ? "" : "s"}
          {d.meta.totalUnanswered > 0 && (
            <>
              {" · "}
              <strong className="text-red-700">{d.meta.totalUnanswered} unanswered</strong>
            </>
          )}
        </span>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        An unanswered question is a trade with nobody in it. This is the one
        number here that says which business to go and find next.
      </p>
      {!cats.length ? (
        <p className="text-sm text-gray-500">Nothing waiting.</p>
      ) : (
        <div className="flex gap-1.5 flex-wrap">
          {cats.map((c, i) => (
            <Badge key={c.code ?? i} variant={(c.unansweredCount ?? 0) > 0 ? "yellow" : "gray"}>
              {c.name ?? c.code}
              {(c.unansweredCount ?? 0) > 0 && ` · ${c.unansweredCount} waiting`}
            </Badge>
          ))}
        </div>
      )}
    </section>
  );
}
